import { useCallback, useState, useMemo, useEffect, useRef } from 'react';
import _ from 'underscore';

import { extendPath, getUrlProtocolAndDomain } from '@bingads-webui/url-util';

export const useImageExperienceAdapterForWebsiteImages = ({
  imageExperienceProps,
  minSize,
  setShowBlocker,
  setImages,
  setMoreImages,
}) => {
  const {
    suggestion,
    finalUrl,
    onUpdateFinalUrl,
    finalUrlProtocol,
    onUpdateFinalUrlPrefix,
    suggestionPoller,
  } = imageExperienceProps;

  const [pageNumber, setPageNumber] = useState(1);
  const [subdomain, setSubdomain] = useState('');
  const [hasMore, setHasMore] = useState(true);
  const lastSuggestionImages = useRef([]);

  const finalUrlWithDomain = useMemo(() => getUrlProtocolAndDomain(finalUrl || ''), [
    finalUrl,
  ]);

  useEffect(() => {
    const baseImages = suggestion.images || [];

    if (!suggestion.inProgress && pageNumber === 1 && lastSuggestionImages.current !== baseImages) {
      setImages(_.map(baseImages, ({ fullUrl, thumbnailUrl }) => ({
        thumbnailSrc: thumbnailUrl,
        fullUrl,
      })));
      setHasMore(suggestion.hasMore);
      lastSuggestionImages.current = baseImages;
    }
  }, [suggestion, setImages, setHasMore, pageNumber]);

  const onSubdomainChange = useCallback((ev, newSubdomain) => {
    setSubdomain(newSubdomain);
  }, [setSubdomain]);

  const onEnteredFinalUrl = useCallback((newUrl, ev) => {
    onUpdateFinalUrl(ev);
  }, [onUpdateFinalUrl]);

  const onUpdateProtocol = useCallback((newProtocol, ev) => {
    onUpdateFinalUrlPrefix(ev, { text: newProtocol });
  }, [onUpdateFinalUrlPrefix]);

  const onSubdomainChanged = useCallback((ev) => {
    const newSubdomain = ev && ev.target && ev.target.value;

    if (!_.isNull(newSubdomain) && !_.isUndefined(newSubdomain)) {
      const fullUrl = _.isEmpty(newSubdomain.trim()) ?
        finalUrl : extendPath(finalUrlWithDomain, newSubdomain);
      setShowBlocker(true);
      setPageNumber(1);
      suggestionPoller.getLandingPageSuggestions({
        landingPageUrl: fullUrl,
        enableOfflinePollingDelay: false,
        forceInternalScrapingService: !!newSubdomain,
        minimumResolution: {
          width: minSize[0],
          height: minSize[1],
        },
        additionalCallback: () => {
          setShowBlocker(false);
        },
      });
      setSubdomain(newSubdomain);
    }
  }, [finalUrl, finalUrlWithDomain, suggestionPoller, setSubdomain, minSize, setShowBlocker]);

  const isLoadingFirstPage = useMemo(() => suggestion && suggestion.inProgress && pageNumber === 1, [
    pageNumber,
    suggestion,
  ]);

  const loadNextPage = useCallback(() => {
    if (hasMore) {
      let url = finalUrl;

      if (subdomain) {
        url = extendPath(finalUrlWithDomain, subdomain);
      }

      const newPageNumber = pageNumber + 1;
      setPageNumber(newPageNumber);
      suggestionPoller.getMoreLandingPageSuggestions({
        landingPageUrl: url,
        pageNumber: newPageNumber,
        forceInternalScrapingService: !!subdomain,
        minimumResolution: {
          width: minSize[0],
          height: minSize[1],
        },
      }).then((data) => {
        const { TotalCount, PageSize, PageNumber } = data.ImagePaging;

        setHasMore(PageNumber * PageSize < TotalCount);

        setMoreImages(_.map(data.Images, item => ({
          thumbnailSrc: item.ThumbnailUrl || item.ImageUrl,
          fullUrl: item.ImageUrl,
        })));
      });
    }
  }, [
    hasMore,
    suggestionPoller,
    minSize,
    subdomain,
    pageNumber,
    finalUrl,
    finalUrlWithDomain,
    setMoreImages,
  ]);

  const websiteImageConfig = useMemo(() => ({
    isLoadingFirstPage: !!isLoadingFirstPage,
    isLoading: !!suggestion.inProgress,
    hasMore,
    finalUrl,
    subdomain,
    onSubdomainChange,
    onSubdomainChanged,
    loadNextPage,
    onEnteredFinalUrl,
    pageSize: 30,
    protocol: finalUrlProtocol,
    onUpdateProtocol,
    showTextAbovePicker: true,
  }), [
    hasMore,
    isLoadingFirstPage,
    suggestion,
    finalUrl,
    subdomain,
    onSubdomainChange,
    onSubdomainChanged,
    loadNextPage,
    onEnteredFinalUrl,
    finalUrlProtocol,
    onUpdateProtocol,
  ]);

  return websiteImageConfig;
};
